import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AppShell from "@/components/AppShell/AppShell"
import AsciiArt from "@/components/AsciiArt/AsciiArt"
import AvatarStack from "@/components/AvatarStack/AvatarStack"
import BreadcrumbBar from "@/components/BreadcrumbBar/BreadcrumbBar"
import Badge from "@/components/Badge/Badge"
import Button from "@/components/Button/Button"
import CustomerModal from "@/components/CustomerModal/CustomerModal"
import type { CustomerField } from "@/components/CustomerModal/CustomerModal"
import FilterPills from "@/components/FilterPills/FilterPills"
import Modal from "@/components/Modal/Modal"
import Pill from "@/components/Pill/Pill"
import SessionRemoveDialog from "@/components/RemoveDialog/SessionRemoveDialog"
import ShareCustomer from "@/components/ShareCustomer/ShareCustomer"
import StatusScreen from "@/components/StatusScreen/StatusScreen"
import TextField from "@/components/TextField/TextField"
import { ARCHIVED_BADGE, ARCHIVE_RESTORE, CUSTOMER_BANNER } from "@/content/archive"
import { NEW_SESSION_BLURB } from "@/content/customers"
import { matchesFilter } from "@/content/status"
import type { StateFilter } from "@/content/status"
import { NAV_CUSTOMERS } from "@/content/nav"
import { useDebouncedField } from "@/hooks/useDebouncedField"
import type { DebouncedField } from "@/hooks/useDebouncedField"
import { useExpiryTick } from "@/hooks/useExpiryTick"
import { invalidateFacilitatorIndex } from "@/hooks/useFacilitatorIndex"
import type { FacilitatorIndex } from "@/hooks/useFacilitatorIndex"
import { useFirestoreSubmit, useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { useCustomerConnection } from "@/hooks/useSessionConnection"
import { restoreCustomer, updateCustomer } from "@/services/customers"
import { getFacilitators } from "@/services/facilitators"
import { createSession, removeSession } from "@/services/sessions"
import { ROUTES } from "@/routes/routes"
import { useAuthStore, selectAuthUid } from "@/store/authStore"
import { useSessionStore } from "@/store"
import { selectCustomerError, selectCustomerFailed, selectCustomerLoading } from "@/store/selectors"
import { shortDate } from "@/utils/relativeTime"
import { sessionPath } from "@/utils/routePaths"
import { sessionState } from "@/utils/sessionLifecycle"
import { removalState } from "@/utils/removal"
import type { CustomerInput, Facilitator, RemovalKind, Session } from "@/types"
import styles from "@/styles/pages/CustomerDetails.module.css"
import { peopleSummary } from "./customerDetailsData"
import SessionCard from "./SessionCard"

const PEOPLE_LABEL = "People"
/* The facilitators the customer is shared with, not the workshop's guests: a
 * participant lives in a session's subcollection, and a roster of them per
 * session would be one collection read per row on a page that lists twenty. */
const PEOPLE_BLURB = "Facilitators who can open this customer"

export default function CustomerDetails() {
  return <AppShell>{(index) => <CustomerScreen index={index} />}</AppShell>
}

function CustomerScreen({ index }: { index: FacilitatorIndex }) {
  const { customerId } = useParams<{ customerId: string }>()
  useCustomerConnection(customerId)

  // Every session read is scoped by the signed-in uid, because the `list` rule
  // is: a query that does not prove membership is refused whole.
  const uid = useAuthStore(selectAuthUid)
  const customer = useSessionStore((s) => s.customer)
  const loading = useSessionStore(selectCustomerLoading)
  const failed = useSessionStore(selectCustomerFailed)
  const failure = useSessionStore(selectCustomerError)
  const navigate = useNavigate()
  const { write } = useFirestoreWrite()

  const [people, setPeople] = useState<Facilitator[]>([])
  const [title, setTitle] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [sharing, setSharing] = useState(false)
  const [filter, setFilter] = useState<StateFilter>("all")
  /* The session the session-dialog is about, and which way it is going. Separate
     state from the customer's: both dialogs can be reached from this screen and
     one variable would make them the same dialog. */
  const [removingSession, setRemovingSession] = useState<{
    session: Session
    kind: RemovalKind
  } | null>(null)
  const { submit, pending, error } = useFirestoreSubmit<string>()
  const removal = useFirestoreSubmit<boolean>()
  /* Its own, not shared with the session dialog below. One hook for both put a
     failed restore's message inside the next session's dialog, and spun the
     banner's button while a session was being archived. */
  const restoring = useFirestoreSubmit<boolean>()

  /*
   * This customer's sessions, out of the list the shell already holds.
   *
   * Filtered here rather than queried: the rules admit no narrower session query
   * than `array-contains facilitatorIds`, so a per-customer call would run the
   * identical scan a second time on every visit, on top of the one the nav had
   * just done. Filtering the copy in hand is the same answer.
   *
   * An archived customer's sessions are NOT in `index.sessions` — they were
   * dropped from the working list by the customer's removal, without anything
   * being written to them. This screen is the record of one customer, so it
   * takes them back: a customer page that said "No sessions yet" about a decade
   * of workshops would be the archive's worst lie.
   *
   * Only the INHERITED ones. A session archived on its own, under a customer who
   * is still active, stays in the Archive where it was filed — this list is
   * still a working list for a working customer.
   */
  const sessions =
    index.sessions === null
      ? null
      : [
          ...index.sessions.filter((session) => session.customerId === customerId),
          ...(index.archivedSessions ?? [])
            .filter((row) => row.inherited && row.session.customerId === customerId)
            .map((row) => row.session),
        ]
  // The composed list, not `index.sessions`: the inherited rows are on screen
  // too and their deadlines can still pass, and the other customers' sessions
  // are not on screen and would schedule renders that change nothing here.
  const now = useExpiryTick((sessions ?? []).map((session) => session.expiresAt))

  // The ids as a stable key: the array identity changes on every snapshot, and
  // depending on it directly would refetch the profiles on every keystroke.
  const memberKey = (customer?.facilitatorIds ?? []).join(",")
  useEffect(() => {
    if (memberKey === "") return
    let live = true
    // Failure is silent on purpose: the People footer is context, and a customer
    // screen that refuses to render because a name did not resolve is worse.
    getFacilitators(memberKey.split(",")).then(
      (loaded) => {
        if (live) setPeople(loaded)
      },
      () => {}
    )
    return () => {
      live = false
    }
  }, [memberKey])

  // One debounced field per detail, each saving only its own key so two people
  // editing different fields cannot overwrite each other.
  const save = (key: keyof CustomerInput) => (value: string) => {
    if (!customerId || !uid) return
    write(() => updateCustomer(customerId, uid, { [key]: value }))
    // The name is on every tile, row and nav entry the shell holds, and those
    // are one-shot reads. Nothing here waits on the answer, so this only says
    // "read again next time" — see `invalidateFacilitatorIndex`.
    if (key === "name") invalidateFacilitatorIndex()
  }
  // Four unconditional calls, collected into one record the shared dialog looks
  // up by key. A hook cannot be called from a loop or a helper.
  const fields: Record<CustomerField, DebouncedField> = {
    name: useDebouncedField({ value: customer?.name ?? "", onCommit: save("name") }),
    sector: useDebouncedField({ value: customer?.sector ?? "", onCommit: save("sector") }),
    size: useDebouncedField({ value: customer?.size ?? "", onCommit: save("size") }),
    about: useDebouncedField({ value: customer?.about ?? "", onCommit: save("about") }),
  }

  if (!customerId) {
    return (
      <StatusScreen nested eyebrow="Customer" title="No customer in the link.">
        <Button onClick={() => navigate(ROUTES.ROOT)}>Back to customers</Button>
      </StatusScreen>
    )
  }

  if (failed) {
    return (
      <StatusScreen
        nested
        eyebrow="Customer"
        title="This screen stopped listening."
        detail="Reload to pick it back up — nothing has been lost."
        raw={failure}
      >
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </StatusScreen>
    )
  }

  if (customer === null) {
    // The listener reports null both while connecting and when there is no such
    // document; the customer's own status tells them apart.
    return loading ? (
      <StatusScreen nested eyebrow="Customer" title="Loading." waiting />
    ) : (
      <StatusScreen
        nested
        eyebrow="Customer"
        title="There is no customer here."
        detail="The link may be stale, or the customer was deleted."
      >
        <Button onClick={() => navigate(ROUTES.ROOT)}>Back to customers</Button>
      </StatusScreen>
    )
  }

  const titleGiven = (title ?? "").trim().length > 0
  const name = customer.name || "Untitled customer"
  const gone = removalState(customer)
  const lastRun = sessions?.[0] ?? null
  const facts: [string, string][] = [
    ["Sector", customer.sector || "—"],
    ["Size", customer.size || "—"],
    ["Sessions", sessions === null ? "—" : String(sessions.length)],
    ["Last run", lastRun ? shortDate(lastRun.updatedAt) : "—"],
  ]
  const meta =
    [customer.sector, customer.size].filter((value) => value.trim() !== "").join(" · ") ||
    "Nothing filled in yet"

  const shown = (sessions ?? []).filter((session) =>
    matchesFilter(sessionState(session, now), filter)
  )

  /*
   * Nothing is claimed on the way in any more. Whether this browser may drive
   * the session is `session.facilitatorIds` containing the signed-in uid, which
   * the session screen reads for itself and the Firestore rules enforce — so a
   * session URL opened from anywhere behaves the same.
   */
  const openSession = (sessionId: string) => navigate(sessionPath(sessionId))

  const facilitatorFor = (session: Session): string | null => {
    // No fallback: naming an arbitrary member as the facilitator of a session
    // they may have nothing to do with is worse than the card's own "—".
    const match = people.find((person) => session.facilitatorIds.includes(person.id))
    return match ? match.displayName || match.email : null
  }

  const undoRemoval = async () => {
    const ok = await restoring.submit(async () => {
      await restoreCustomer(customerId)
      return true
    })
    if (ok) {
      invalidateFacilitatorIndex()
      index.reload()
    }
  }

  const applySessionRemoval = async () => {
    if (!removingSession || !uid) return
    const { session, kind } = removingSession
    const ok = await removal.submit(async () => {
      await removeSession(session.id, uid, kind)
      return true
    })
    if (ok) {
      setRemovingSession(null)
      // This screen renders the list the write just changed, so it has to read
      // again rather than only marking the index stale.
      invalidateFacilitatorIndex()
      index.reload()
    }
  }

  const create = async () => {
    if (!titleGiven) return
    const id = await submit(() => createSession(customerId, (title ?? "").trim()))
    if (id) {
      setTitle(null)
      // The shell's list is what this screen reads, and it is not a listener:
      // without this, coming back from the new session would show a customer
      // that has one fewer session than it does.
      index.reload()
      openSession(id)
    }
  }

  return (
    <>
      <BreadcrumbBar
        trail={[{ label: NAV_CUSTOMERS, to: ROUTES.ROOT }, { label: name }]}
        /* One action, and only while the customer is present. No new session
           against an archived one: the write would land — the rules only test
           membership — and it would produce a session immediately invisible in
           every list, because its customer's removal hides it. Restore first.

           Archive and Delete are deliberately NOT here. They belong to the
           customer tile in the list, which is the screen for choosing between
           customers; this one is for working inside a single customer, and the
           same pair in both places was one act with two homes. Restore is the
           exception — the banner below is the only place it exists. */
        action={
          gone === "active" ? <Button onClick={() => setTitle("")}>New session</Button> : null
        }
      />

      {gone !== "active" ? (
        <div className={styles.banner}>
          <div className={styles.bannerText}>
            <Pill tone="warning">{ARCHIVED_BADGE[gone]}</Pill>
            <span>{CUSTOMER_BANNER[gone]}</span>
          </div>
          {gone === "archived" ? (
            <Button variant="quiet" loading={restoring.pending} onClick={undoRemoval}>
              {ARCHIVE_RESTORE}
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* A failed restore said nothing here before, and its message surfaced in
          the next dialog instead. It belongs beside the button that failed. */}
      {restoring.error !== null ? <p className={styles.error}>{restoring.error}</p> : null}

      <div className={styles.split}>
        <aside className={styles.profile}>
          <div className={styles.hero}>
            <AsciiArt motif="sprout" family={2} size="lg" />
          </div>

          <div className={styles.profileBody}>
            <div className={styles.identityRow}>
              <h1 className={styles.name}>{customer.name || "Untitled customer"}</h1>
              <button
                type="button"
                className={styles.edit}
                onClick={() => setEditing(true)}
                title="Edit details"
                aria-label="Edit details"
              >
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M11.2 2.3a1.6 1.6 0 0 1 2.3 2.3L5.6 12.4l-3 .9.9-3 7.7-8Z"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {customer.about.trim() ? <p className={styles.about}>{customer.about}</p> : null}

            <dl className={styles.facts}>
              {facts.map(([key, value]) => (
                <div className={styles.fact} key={key}>
                  <dt>{key}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>

            <div className={styles.people}>
              <p className={styles.peopleLabel}>{PEOPLE_LABEL}</p>
              <button
                type="button"
                className={styles.peopleRow}
                title={PEOPLE_BLURB}
                onClick={() => setSharing(true)}
              >
                <AvatarStack
                  people={people.map((person) => ({
                    id: person.id,
                    name: person.displayName || person.email,
                  }))}
                  size={28}
                  max={4}
                />
                <span className={styles.peopleNames}>{peopleSummary(people)}</span>
              </button>
            </div>
          </div>
        </aside>

        <section className={styles.sessions}>
          <div className={styles.sessionsHead}>
            <h2 className={styles.sessionsTitle}>Sessions</h2>
            <FilterPills value={filter} onChange={setFilter} omit={["draft"]} />
          </div>

          {index.error !== null && sessions === null ? (
            <div className={styles.empty}>
              <p className={styles.error}>{index.error}</p>
              <Button variant="quiet" loading={index.loading} onClick={index.reload}>
                Try again
              </Button>
            </div>
          ) : sessions === null ? (
            <div className={styles.empty}>Loading sessions…</div>
          ) : shown.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyTitle}>
                {sessions.length === 0 ? "No sessions yet." : "Nothing matches that filter."}
              </div>
              {sessions.length === 0 ? (
                <div className={styles.emptyBody}>Check the customer details, then start one.</div>
              ) : null}
            </div>
          ) : (
            <ul className={styles.cards}>
              {shown.map((session) => (
                <li key={session.id}>
                  <SessionCard
                    session={session}
                    customer={customer}
                    facilitatorName={facilitatorFor(session)}
                    onOpen={() => openSession(session.id)}
                    onRemove={
                      gone === "active"
                        ? (kind) => setRemovingSession({ session, kind })
                        : undefined
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {editing ? (
        // Each field saves on a debounce, so Done just closes the dialog.
        <CustomerModal
          mode="edit"
          binding={(key) => fields[key]}
          onClose={() => setEditing(false)}
          actions={<Button onClick={() => setEditing(false)}>Done</Button>}
        />
      ) : null}

      {sharing ? (
        <Modal
          title={PEOPLE_LABEL}
          description={PEOPLE_BLURB}
          width="narrow"
          dismissible
          onClose={() => setSharing(false)}
          actions={<Button onClick={() => setSharing(false)}>Done</Button>}
        >
          <ShareCustomer customer={customer} />
        </Modal>
      ) : null}

      {removingSession !== null ? (
        <SessionRemoveDialog
          session={removingSession.session}
          kind={removingSession.kind}
          error={removal.error}
          pending={removal.pending}
          /* The error belongs to the dialog that produced it — see the same
             clear on the customer list. */
          onClose={() => {
            setRemovingSession(null)
            removal.clearError()
          }}
          onConfirm={() => void applySessionRemoval()}
        />
      ) : null}

      {title !== null ? (
        <Modal
          title="New session"
          description={NEW_SESSION_BLURB}
          dismissible
          onClose={() => setTitle(null)}
          actions={
            <>
              <Button variant="quiet" onClick={() => setTitle(null)}>
                Cancel
              </Button>
              <Button disabled={!titleGiven} loading={pending} onClick={create}>
                {pending ? "Creating…" : "Create session"}
              </Button>
            </>
          }
        >
          <TextField
            label="Session name"
            placeholder="Customs desk"
            strong
            autoFocus
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />

          <div>
            <div className={styles.fieldLabel}>Customer</div>
            <div className={styles.customerCard}>
              <Badge name={customer.name} size={28} />
              <div className={styles.customerCardText}>
                <div className={styles.customerCardName}>
                  {customer.name || "Untitled customer"}
                </div>
                <div className={styles.customerCardMeta}>{meta}</div>
              </div>
            </div>
          </div>

          {error !== null ? <p className={styles.error}>{error}</p> : null}
        </Modal>
      ) : null}
    </>
  )
}
